import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { errorMessage, patch } from "../api";
import { Alert, Button, Field, PageHeader, Panel, PanelHeader } from "../components/ui";
import { useSession } from "../session";
import { MyCalendarsCard } from "../components/my-calendars";
import { GmailCard } from "../components/gmail-card";
import { ContactsSyncCard } from "../components/contacts-sync";
import { GOOGLE_RESULTS } from "./Calendar";
import { MySignature } from "./settings/MySignature";
import { NotificationSettings } from "./settings/Notifications";

/** My profile: your name, mobile, and color; calendars; contacts on your phone; notifications; and your signature. */
export function ProfilePage() {
  const { me, refresh, can } = useSession();
  const [params, setParams] = useSearchParams();
  const google = GOOGLE_RESULTS[params.get("google") ?? ""];
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", color: "#D71920" });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (me) setForm({ firstName: me.user.firstName, lastName: me.user.lastName, phone: me.user.phone, color: me.user.color });
  }, [me]);
  const save = useMutation({
    mutationFn: () => patch("/api/me", { firstName: form.firstName, lastName: form.lastName, phone: form.phone, color: form.color }),
    onSuccess: async () => {
      await refresh();
      setSaved(true);
    },
  });
  if (!me) return null;
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <PageHeader title="My profile" description={me.user.email} />
      {google && (
        <Alert tone={google.tone}>
          {google.text}{" "}
          <button className="font-semibold underline" onClick={() => setParams({}, { replace: true })}>
            Dismiss
          </button>
        </Alert>
      )}

      <Panel>
        <PanelHeader title="Your details" description="Your name as it shows on jobs, tasks, and documents; your mobile; and your color on the calendar. Your email is your Google sign-in." />
        <form
          className="grid gap-4 px-5 py-5 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSaved(false);
            save.mutate();
          }}
        >
          <Field label="First name" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          <Field label="Last name" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          <Field label="Mobile" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <div>
            <label htmlFor="my-color" className="mb-1 block text-sm font-semibold text-ink-soft">
              Calendar color
            </label>
            <input id="my-color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value.toUpperCase() })} className="h-10 w-14 cursor-pointer rounded border border-line" />
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <Button type="submit" variant="primary" busy={save.isPending}>
              Save
            </Button>
            {saved && !save.isPending && <span className="text-sm text-ok">Saved.</span>}
            {save.error && <Alert>{errorMessage(save.error)}</Alert>}
          </div>
        </form>
      </Panel>

      <MyCalendarsCard />
      {can("customers.view") && <ContactsSyncCard />}

      {can("fred.use") && <GmailCard />}
      <NotificationSettings embedded />
      <MySignature embedded />
    </div>
  );
}
